// web/src/screens/admin/sessions_detail.ts
//
// One game session: a header card (who/which game/when/eval counts/cost) plus
// the attempts table. v2-aligned rebuild of v1's session_detail.gohtml — v1's
// FSM fields (current_phase / current_card / attempt_count / max_tries) are
// gone in the v2 schema and are not shown.
//
// All API-supplied text via textContent only (T-11-09a).

import { renderAdminShell } from "./admin_shell.js";
import { adminGet, AdminError } from "../../api/admin.js";
import type { AdminAttemptSummaryDTO, AdminSessionDetailDTO, AdminRouteProps } from "../../types/api.js";
import {
  fmtMicrousd, fmtTimestamp, statusPill, kvList, code, table, adminLink, errMessage, evalColors, pill,
} from "./logs_common.js";

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = props as AdminRouteProps | undefined;
  const sessionID = p && p.kind === "admin-session-detail" ? p.sessionID : "";

  const main = renderAdminShell(root, [
    { label: "Game log", href: "/admin/sessions" },
    { label: sessionID || "?" },
  ]);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const headerWrap = document.createElement("section");
  headerWrap.setAttribute("style", "background: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; margin-bottom: 16px;");
  main.appendChild(headerWrap);

  const attemptsHeading = document.createElement("h2");
  attemptsHeading.setAttribute("style", "margin: 0 0 12px 0; font-size: 15px; font-weight: 600;");
  main.appendChild(attemptsHeading);

  const attemptsWrap = document.createElement("div");
  attemptsWrap.setAttribute("style", "overflow-x: auto;");
  main.appendChild(attemptsWrap);

  function renderHeader(s: AdminSessionDetailDTO): void {
    headerWrap.replaceChildren();
    const userBits = [s.user_name, s.user_age != null ? `age ${s.user_age}` : "", s.user_sex ?? ""].filter(Boolean).join(" · ");
    headerWrap.appendChild(kvList([
      ["Session ID", code(s.id)],
      ["Started", fmtTimestamp(s.started_at)],
      ["Finished", s.finished_at ? fmtTimestamp(s.finished_at) : "—"],
      ["Status", statusPill(s.status)],
      ["Game", `${s.game_title || s.game_short_code} (${s.game_short_code})`],
      ["User", userBits || "—"],
      ["Distinct cards", String(s.distinct_cards)],
      ["Correct / Partial / Incorrect / Silence", `${s.correct_count} / ${s.partial_count} / ${s.incorrect_count} / ${s.silence_count}`],
      ["Total cost", code(fmtMicrousd(s.total_cost_microusd))],
    ]));
  }

  function renderAttempts(rows: AdminAttemptSummaryDTO[]): void {
    attemptsHeading.textContent = `Attempts (${rows.length})`;
    attemptsWrap.replaceChildren();
    if (rows.length === 0) {
      const empty = document.createElement("p");
      empty.textContent = "No attempts yet.";
      empty.setAttribute("style", "color: #6b7280; font-size: 14px;");
      attemptsWrap.appendChild(empty);
      return;
    }
    const t = table(["#", "Time", "Task", "Transcript", "Eval", "Guard", "Cost", "STT ms", "LLM ms", "TTS ms", "Total ms", "Net ms", "Audio"]);
    for (const a of rows) {
      const ev = evalColors(a.evaluation);
      const transcript = document.createElement("span");
      transcript.textContent = a.user_speech_raw || "—";
      transcript.title = a.user_speech_raw || "";
      transcript.setAttribute("style", "display: inline-block; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom; color: #4b5563;");
      const audioCell: string | Node = a.has_audio
        ? adminLink("audio", `/api/admin/attempts/${encodeURIComponent(a.id)}/audio`)
        : "—";
      // Note: the audio link is a real same-origin GET (the <audio> player on
      // the attempt-detail screen streams it); a plain <a> opens it directly.
      t.addRow([
        adminLink(`#${a.attempt_number}`, `/admin/attempts/${encodeURIComponent(a.id)}`),
        fmtTimestamp(a.created_at),
        a.task_short_code,
        transcript,
        pill(a.evaluation, ev.fg, ev.bg),
        a.guard_reason ?? "—",
        fmtMicrousd(a.total_cost_microusd),
        String(a.stt_latency_ms),
        String(a.llm_latency_ms),
        String(a.tts_latency_ms),
        a.server_total_ms != null ? String(a.server_total_ms) : "—",
        a.client_network_ms != null ? String(a.client_network_ms) : "—",
        audioCell,
      ]);
    }
    attemptsWrap.appendChild(t.el);
  }

  async function load(): Promise<void> {
    try {
      const [s, attempts] = await Promise.all([
        adminGet<AdminSessionDetailDTO>(`/api/admin/sessions/${encodeURIComponent(sessionID)}`),
        adminGet<AdminAttemptSummaryDTO[]>(`/api/admin/sessions/${encodeURIComponent(sessionID)}/attempts`),
      ]);
      status.textContent = "";
      status.remove();
      renderHeader(s);
      renderAttempts(attempts);
    } catch (err) {
      if (err instanceof AdminError && err.status === 404) {
        status.textContent = `Session ${sessionID} not found.`;
        return;
      }
      status.textContent = errMessage(err, "load session");
    }
  }

  void load();
  return () => {};
}
